<template>
    <v-container v-if="loading">
        <loading-spinner/>
    </v-container>
    <div v-else-if="assignedExercise">
        <!-- BACK BUTTON -->
        <back-button class="mt-1" fixed/>
        <!-- BLOCK TITLE -->
        <v-row class="block-separator">
            <v-col cols="12" class="title-separator">
                {{ $tc('common.blocks', 1) }} {{ block }}
            </v-col>
        </v-row>
        <v-container class="mt-3">
            <!-- BASIC INFO EXERCISE -->
            <v-row>
                <v-col cols="12" class="text-center">
                    <v-chip
                        v-for="category in assignedExercise.categories"
                        :key="category"
                        outlined label
                        class="mr-1 mb-1"
                        small color="#333333"
                    >
                        {{ category }}
                    </v-chip>
                    <v-chip
                        v-for="type in assignedExercise.types"
                        :key="type"
                        outlined label
                        class="mr-1 mb-1"
                        small color="#333333"
                    >
                        {{ type }}
                    </v-chip>
                </v-col>
                <v-col cols="12" class="text-center">
                    <div class="name-exercise text-uppercase">
                        {{ assignedExercise.name }}
                    </div>
                    <div class="duration-exercise">
                        <v-icon size="13">mdi-repeat</v-icon>
                        {{ setsString }}
                    </div>
                </v-col>
            </v-row>
            <v-row>
                <!-- VIDEO PLAYER -->
                <v-col cols="12">
                    <video-player
                        v-if="video"
                        ref="videoPlayer"
                        :options="playerOptions"
                    />
                    <v-img
                        v-else eager contain
                        :lazy-src="imgPlaceholder"
                        :src="exerciseImg"
                        class="exercise-img"
                    />
                </v-col>
                <!-- DESCRIPTION EXERCISE -->
                <v-col cols="12" v-if="exercise.description" class="exercise-description">
                    {{ exercise.description }}
                </v-col>
                <!-- COMMENTARY EXERCISE -->
                <v-col cols="12" v-if="assignedExercise.commentary">
                    <v-alert type="info" color="accent" outlined>
                        <b>{{ $t('exercises.comment_of_coach') }}:</b> {{ assignedExercise.commentary }}
                    </v-alert>
                </v-col>
                <!-- COMMENTARY TO COACH -->
                <v-col cols="12">
                    <kimsa-textarea-field
                        :label="$t('common.message_to_coach')"
                        :placeholder="$t('common.send_placeholder')"
                        @change="commentaryFromStudent = $event"
                        :value="commentaryFromStudent"
                        outlined rows="2"
                    />
                    <div class="text-right">
                        <v-btn
                            class="normal-btn mt-3"
                            small depressed dark
                            color="backGroundPrimary"
                            @click="sendToCoach"
                            :loading="loadingCommentary"
                        >
                            {{ $t('common.send') }}
                        </v-btn>
                    </div>
                </v-col>
                <!-- EDIT SETS -->
                <v-col cols="12">
                    <edit-sets
                        class="ma-0"
                        :assigned-routine-id="assignedRoutineId"
                        :exercise-id="exerciseId"
                        :config="config"
                        :block="block"
                        :blocks="currentRoutine.blocks"
                        :category="category"
                        @updatedSet="setAssignedRoutine()"
                    />
                </v-col>
            </v-row>
            <v-sheet v-if="!isMobile" color="transparent" height="72"/>

            <routine-footer
                :percent="percentReady"
                ref="routineFooter"
            />
        </v-container>

    </div>
</template>

<script>

import {db, Timestamp} from '@/utils/Firebase'
import {mapState, mapMutations} from 'vuex'
import {getMergedBlocks} from "@/utils/Exercises";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import VideoPlayer from "@/components/common/VideoPlayer";
import EditSets from "@/components/student/EditSets";
import RoutineFooter from '@/components/common/routines/RoutineFooter'
import BackButton from "@/components/common/BackButton";
import KimsaTextareaField from "@/components/common/inputs/KimsaTextareaField";

export default {
    components: {
        KimsaTextareaField,
        BackButton,
        LoadingSpinner,
        VideoPlayer,
        EditSets,
        RoutineFooter,
    },
    data() {
        return {
            loading: false,
            assignedRoutine: null,
            routine: null,
            exercise: null,
            block: 0,
            commentaryFromStudent: '',
            loadingCommentary: false,
            playerOptions: {
                fluid: true,
                language: 'es',
                controls: true,
                autoplay: false,
                poster: null,
                sources: [{
                    type: 'application/x-mpegURL',
                    src: null,
                }],
            },
        }
    },
    computed: {
        ...mapState(['currentRoutine', 'currentExercise']),
        assignedExercise(vm = this) {
            return vm.currentRoutine?.blocks[vm.block - 1]?.exercises?.find(ex => ex.objectID === vm.exerciseId) || null
        },
        config(vm = this) {
            return vm.assignedExercise?.config || null
        },
        category(vm = this) {
            return vm.exercise?.categories[0] || null
        },
        setsCount(vm = this) {
            return vm.config?.sets?.length || 0
        },
        setsString(vm = this) {
            return `${vm.setsCount} ${vm.$tc('exercises.sets', (vm.setsCount > 1 ? 2 : 1))}`
        },
        assignedRoutineId(vm = this) {
            return vm.$route.params.uid
        },
        exerciseId(vm = this) {
            return vm.$route.params.id
        },
        percentReady(vm = this) {
            if (!vm.currentRoutine) return 0
            let qtyExercises = 0
            let completed = 0

            vm.currentRoutine.blocks.forEach(block => {
                let exercises = block.exercises.filter(ex => ex.type !== 'rest').length
                if (block.completed) {
                    qtyExercises += exercises
                    completed += exercises
                } else qtyExercises += exercises
            })

            return parseInt((completed * 100) / qtyExercises) || 0
        },
        blocksLength(vm = this) {
            return vm.currentRoutine?.blocks?.length || 0
        },
        video(vm = this) {
            if (!vm.currentExercise) return null
            const {video} = vm.currentExercise
            if (!video || !video.ready) return null
            vm.playerOptions.sources[0].src = video.url || null
            return video
        },
        exerciseImg(vm = this) {
            let exerciseImg = vm.exercise?.img || vm.exercise?.video?.thumb || vm.imgPlaceholder
            vm.playerOptions.sources[0].src = exerciseImg
            return exerciseImg
        },
        studentId(vm = this) {
            return vm.currentUser.uid || null
        },
        coachId(vm = this) {
            return vm.account?.studentSubscription?.coachId || null
        }
    },
    async mounted() {
        const vm = this
        vm.loading = true
        if (!vm.currentRoutine) await vm.setAssignedRoutine()
        await vm.setExercise()
        vm.loading = false
    },
    methods: {
        ...mapMutations(['setCurrentRoutine', 'setCurrentExercise', 'setTitle', 'setCurrentCarousel']),
        async setAssignedRoutine() {
            const vm = this
            let assignedRoutineRef = await db.collection('assignedRoutines').doc(vm.assignedRoutineId).get()
            vm.assignedRoutine = assignedRoutineRef.data()

            if (vm.assignedRoutine.routineId) {
                let routineRef = await db.collection('routines').doc(vm.assignedRoutine.routineId).get()
                vm.routine = routineRef.data()
            }

            let routineName = vm.assignedRoutine?.name || vm.routine?.name || vm.$t('common.not_available_short')
            vm.setTitle(routineName)

            let currentRoutine = {
                assignedRoutineId: vm.assignedRoutineId,
                name: routineName,
                img: vm.assignedRoutine?.img || vm.routine?.img || null,
                description: vm.routine?.description || null,
                ...vm.assignedRoutine
            }

            currentRoutine.blocks = await getMergedBlocks(vm.assignedRoutine.blocks)
            await vm.setCurrentRoutine(currentRoutine)
            if (!vm.exercise) await vm.setExercise()
        },
        async setExercise() {
            const vm = this
            if (!vm.currentRoutine) await vm.setAssignedRoutine()
            let exerciseRef = await db.collection('exercises').doc(vm.exerciseId).get()
            vm.exercise = {id: vm.exerciseId, ...exerciseRef.data()}

            vm.currentRoutine.blocks.forEach((block, i) => {
                block.exercises.forEach((exercise, j) => {
                    if (exercise.id === vm.exerciseId) vm.block = i + 1
                })
            })

            vm.setCurrentExercise(vm.exercise)
        },
        controlVideo() {
            const vm = this
            vm.$refs.player.play();
        },
        async sendToCoach() {
            const vm = this
            vm.loadingCommentary = true
            let blocks = JSON.parse(JSON.stringify(vm.currentRoutine.blocks))
            let exerciseIndex = blocks[vm.block - 1].exercises.findIndex(ex => ex.id === vm.exerciseId)
            blocks[vm.block - 1].exercises[exerciseIndex].commentaryFromStudent = vm.commentaryFromStudent
            await db.collection('assignedRoutines').doc(vm.assignedRoutineId).update({blocks: blocks})
            await vm.sendMessageToCoach()
            vm.toast(vm.$t('exercises.commentary_from_student_sent'))
            await vm.setAssignedRoutine()
            vm.loadingCommentary = false
        },
        async sendMessageToCoach() {
            const vm = this

            let message = `${vm.$t('common.date')}: ${vm.$moment().format('DD-MM-YYYY')} \n${vm.$tc('exercises.exercises', 1)}: ${vm.assignedExercise.name} \n${vm.$t('common.comment')}: ${vm.commentaryFromStudent}`

            let chatRef = await db.collection('privateChats')
                .where('type', '==', 'coach_student')
                .where('participants', 'in', [[vm.coachId, vm.studentId]])
                .get()

            let chatId = null
            if (chatRef.docs.length > 0) {
                chatId = chatRef.docs[0].id
            } else {
                let newChat = await db.collection('privateChats').add({
                    type: 'coach_student',
                    participants: [vm.coachId, vm.studentId]
                })
                chatId = newChat.id
            }

            let payload = {
                message: message,
                from: vm.studentId,
                read: false,
                timestamp: Timestamp.now(),
                type: 'text'
            }

            await db.collection('privateChats').doc(chatId).collection('messages').add(payload)
            await db.collection('privateChats').doc(chatId).update({lastMessage: payload})
        },
    },
    watch: {
        assignedExercise(val) {
            if (val?.commentaryFromStudent) this.commentaryFromStudent = val.commentaryFromStudent
        }
    },
    beforeRouteLeave(to, from, next) {
        const vm = this
        if (to.name !== 'student.routine.detail') {
            vm.setCurrentRoutine(null)
            vm.setCurrentExercise(null)
            vm.setCurrentCarousel(null)
            vm.setTitle('')
        }
        next()
    }
}
</script>

<style scoped>

.block-separator {
    background-color: var(--v-backGroundPrimary-base);
    color: var(--v-primary-base);
}

.title-separator {
    font-size: 17px;
    text-align: center;
}

.block-number {
    position: absolute;
    color: #FFF;
    margin-right: 20px;
    font-size: 11px;
    top: 0;
    right: 0;
}

.name-exercise {
    font-size: 20px;
    line-height: 24px;
    font-weight: bold;
    color: #333333;
}

.duration-exercise {
    font-size: 13px;
    line-height: 17px;
    color: #666666;
}

.exercise-description {
    font-size: 12px;
    line-height: 16px;
    color: #666666;
    text-align: center;
}

.exercise-img {
    border-radius: 8px;
}
</style>